import { useEffect, useState } from 'react'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/ui/Modal'
import type { AddWorkspaceMemberRequest, WorkspaceRole } from '../types'

export interface AddMemberModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onAdd: (request: AddWorkspaceMemberRequest) => Promise<void>
}

/** Assign/share modal (components.md "Assign-to-workspace modal" /
 * screens.md §8's membership-dependent actions). No user-search endpoint
 * exists in the frozen contract yet — this takes a user id/email directly,
 * matching `AddWorkspaceMemberRequest`'s `userId` field. */
export function AddMemberModal({ open, onClose, onAdd }: AddMemberModalProps) {
  const [userId, setUserId] = useState('')
  const [role, setRole] = useState<WorkspaceRole>('member')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) {
      setUserId('')
      setRole('member')
    }
  }, [open])

  async function handleSubmit() {
    setBusy(true)
    try {
      await onAdd({ userId: userId.trim(), workspaceRole: role })
      onClose()
    } catch {
      // The caller (WorkspacePage's `runMutation`) already surfaces the
      // failure via the shared toast — swallow here so the modal simply
      // stays open (letting the user retry) instead of this becoming an
      // unhandled rejection.
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a member"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={busy} disabled={userId.trim().length === 0}>
            Add member
          </Button>
        </>
      }
    >
      <label className="new-workspace-field" htmlFor="add-member-user-id">
        <span>User ID or email</span>
        <input
          id="add-member-user-id"
          type="text"
          value={userId}
          onChange={(event) => setUserId(event.target.value)}
        />
      </label>

      <fieldset className="new-workspace-visibility">
        <legend>Role</legend>
        <label>
          <input
            type="radio"
            name="add-member-role"
            checked={role === 'member'}
            onChange={() => setRole('member')}
          />
          Member
        </label>
        <label>
          <input
            type="radio"
            name="add-member-role"
            checked={role === 'owner'}
            onChange={() => setRole('owner')}
          />
          Owner
        </label>
      </fieldset>
    </Modal>
  )
}
